import classNames from "classnames";
import Image from "next/legacy/image";
import React, { useEffect, useRef, useState } from "react";
import classes from "./Hero.module.scss";
import TranslatedLink from "components/utilities/TranslatedLink";
import { Button } from "react-bootstrap";
import { useTranslation } from "next-i18next";

interface Props extends React.ComponentPropsWithoutRef<"div"> {
  altText: string;
  title: string;
}

const firstImageUrl = "/team-header1.webp";
const secondImageUrl = "/team-header2.webp";

const HeroImage = ({ altText, title, className, ...htmlProps }: Props) => {
  const { t } = useTranslation("team");
  const [currentImageUrl, setCurrentImageUrl] = useState(firstImageUrl);
  const [verticalImagePosition, setVerticalImagePosition] =
    useState<number>(30);
  const imageWrapperRef = useRef<HTMLDivElement | null>(null);

  const handleIntersection: IntersectionObserverCallback = (entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) {
        setCurrentImageUrl(secondImageUrl);
        setVerticalImagePosition(85);
      }
    });
  };

  useEffect(() => {
    const observer = new IntersectionObserver(handleIntersection, {
      rootMargin: "300px",
      threshold: 0,
    });

    if (imageWrapperRef.current) {
      observer.observe(imageWrapperRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [imageWrapperRef]);

  return (
    <div {...htmlProps} className={classNames(classes.hero, className)}>
      <div className={classes.hero__imageWrapper} ref={imageWrapperRef}>
        <Image
          src={currentImageUrl}
          alt={altText}
          layout="responsive"
          width={100}
          height={50}
          objectFit="cover"
          objectPosition={`0% ${verticalImagePosition}%`}
        />
        <div className={classes.hero__showMore}>
          <TranslatedLink href="/team#showMoreButton">
            <Button
              id="showMoreButton"
              size="sm"
              className="me-3"
              variant="primary"
              aria-label={t("hero.show_more")}
            >
              <p className="m-0 fs-5">{t("hero.show_more")}</p>
            </Button>
          </TranslatedLink>
        </div>
      </div>
      <div className={classes.hero__text}>
        <h1>{title}</h1>
      </div>
    </div>
  );
};

export default HeroImage;
